import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactSection = ({ embedded = false }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    interest: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, interest: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        interest: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const interests = t('contact.form.interests');

  // Embedded version - just the form
  if (embedded) {
    return (
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#1A1F4B] mb-1.5">
              {t('contact.form.name')} *
            </label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              data-testid="contact-name"
              className="bg-white border-gray-200 focus:border-[#C9A84C] focus:ring-[#C9A84C]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1A1F4B] mb-1.5">
              {t('contact.form.email')} *
            </label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              data-testid="contact-email"
              className="bg-white border-gray-200 focus:border-[#C9A84C] focus:ring-[#C9A84C]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#1A1F4B] mb-1.5">
              {t('contact.form.phone')} *
            </label>
            <Input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              data-testid="contact-phone"
              className="bg-white border-gray-200 focus:border-[#C9A84C] focus:ring-[#C9A84C]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1A1F4B] mb-1.5">
              {t('contact.form.company')}
            </label>
            <Input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              data-testid="contact-company"
              className="bg-white border-gray-200 focus:border-[#C9A84C] focus:ring-[#C9A84C]"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#1A1F4B] mb-1.5">
            {t('contact.form.interest')} *
          </label>
          <Select value={formData.interest} onValueChange={handleSelectChange}>
            <SelectTrigger data-testid="contact-interest" className="bg-white border-gray-200">
              <SelectValue placeholder={t('contact.form.interest')} />
            </SelectTrigger>
            <SelectContent>
              {interests.map((interest, index) => (
                <SelectItem key={index} value={interest}>
                  {interest}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#1A1F4B] mb-1.5">
            {t('contact.form.message')} *
          </label>
          <Textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={4}
            data-testid="contact-message"
            className="bg-white border-gray-200 focus:border-[#C9A84C] focus:ring-[#C9A84C]"
          />
        </div>

        {/* Status Messages */}
        {status === 'success' && (
          <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded" data-testid="contact-success">
            <CheckCircle className="w-5 h-5" />
            <span>{t('contact.form.success')}</span>
          </div>
        )}
        {status === 'error' && (
          <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded" data-testid="contact-error">
            <AlertCircle className="w-5 h-5" />
            <span>{t('contact.form.error')}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="contact-submit"
          className="w-full bg-[#1A1F4B] text-white py-3 font-semibold flex items-center justify-center gap-2 hover:bg-[#C9A84C] hover:text-[#1A1F4B] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Send className="w-5 h-5" />
              {t('contact.form.submit')}
            </>
          )}
        </button>
      </form>
    );
  }

  // Full section version (for homepage)
  return (
    <section 
      id="contact" 
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#F9FAFB]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-[2px] bg-[#C9A84C]" />
            <span className="text-[#C9A84C] font-semibold text-sm tracking-wider uppercase">
              Contact
            </span>
            <div className="w-12 h-[2px] bg-[#C9A84C]" />
          </div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-[#1A1F4B] mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-[#1F2937]/70 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#1A1F4B] mb-2">
                    {t('contact.form.name')} *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name"
                    className="bg-white border-gray-200 focus:border-[#C9A84C] focus:ring-[#C9A84C]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1F4B] mb-2">
                    {t('contact.form.email')} *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email"
                    className="bg-white border-gray-200 focus:border-[#C9A84C] focus:ring-[#C9A84C]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#1A1F4B] mb-2">
                    {t('contact.form.phone')} *
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="contact-phone"
                    className="bg-white border-gray-200 focus:border-[#C9A84C] focus:ring-[#C9A84C]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1F4B] mb-2">
                    {t('contact.form.company')}
                  </label>
                  <Input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    data-testid="contact-company"
                    className="bg-white border-gray-200 focus:border-[#C9A84C] focus:ring-[#C9A84C]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1F4B] mb-2">
                  {t('contact.form.interest')} *
                </label>
                <Select value={formData.interest} onValueChange={handleSelectChange}>
                  <SelectTrigger data-testid="contact-interest" className="bg-white border-gray-200">
                    <SelectValue placeholder={t('contact.form.interest')} />
                  </SelectTrigger>
                  <SelectContent>
                    {interests.map((interest, index) => (
                      <SelectItem key={index} value={interest}>
                        {interest}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1F4B] mb-2">
                  {t('contact.form.message')} *
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  data-testid="contact-message"
                  className="bg-white border-gray-200 focus:border-[#C9A84C] focus:ring-[#C9A84C]"
                />
              </div>

              {/* Status Messages */}
              {status === 'success' && (
                <div className="flex items-center gap-2 text-green-600 bg-green-50 p-4" data-testid="contact-success">
                  <CheckCircle className="w-5 h-5" />
                  <span>{t('contact.form.success')}</span>
                </div>
              )}
              {status === 'error' && (
                <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4" data-testid="contact-error">
                  <AlertCircle className="w-5 h-5" />
                  <span>{t('contact.form.error')}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="w-full bg-[#1A1F4B] text-white py-4 font-semibold flex items-center justify-center gap-2 hover:bg-[#C9A84C] hover:text-[#1A1F4B] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    {t('contact.form.submit')}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Map & Info */}
          <div className="space-y-8">
            {/* Static Map */}
            <div className="relative h-[300px] bg-[#1A1F4B] overflow-hidden" data-testid="contact-map">
              {/* Map Background Pattern */}
              <div className="absolute inset-0 opacity-20">
                <svg viewBox="0 0 800 400" className="w-full h-full">
                  <defs>
                    <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                      <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#C9A84C" strokeWidth="0.5"/>
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />
                </svg>
              </div>

              {/* Location Markers */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-full max-w-md">
                  {/* Brazil Marker */}
                  <div className="absolute left-1/4 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                    <div className="w-12 h-12 bg-[#C9A84C] rounded-full flex items-center justify-center mx-auto mb-2 pulse-node">
                      <MapPin className="w-6 h-6 text-[#1A1F4B]" />
                    </div>
                    <span className="text-white text-xs font-medium">Vila Velha, BR</span>
                  </div>

                  {/* USA Marker */}
                  <div className="absolute right-1/4 top-1/3 translate-x-1/2 -translate-y-1/2 text-center">
                    <div className="w-12 h-12 bg-[#C9A84C] rounded-full flex items-center justify-center mx-auto mb-2 pulse-node">
                      <MapPin className="w-6 h-6 text-[#1A1F4B]" />
                    </div>
                    <span className="text-white text-xs font-medium">Charleston, USA</span>
                  </div>

                  {/* Connection Line */}
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 200">
                    <path
                      d="M100,100 Q200,50 300,66"
                      fill="none"
                      stroke="#C9A84C"
                      strokeWidth="2"
                      strokeDasharray="5,5"
                      opacity="0.5"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Contact Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Brazil */}
              <div className="bg-white p-6 border-l-4 border-[#C9A84C]" data-testid="contact-brazil">
                <h3 className="font-bold text-[#1A1F4B] mb-4 flex items-center gap-2">
                  <span className="text-xl">🇧🇷</span>
                  {t('contact.brazil.title')}
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3 text-[#1F2937]/70">
                    <Phone className="w-4 h-4 text-[#C9A84C]" />
                    <span>{t('contact.brazil.phone')}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#1F2937]/70">
                    <Mail className="w-4 h-4 text-[#C9A84C]" />
                    <span>{t('contact.brazil.email')}</span>
                  </div>
                </div>
              </div>

              {/* USA */}
              <div className="bg-white p-6 border-l-4 border-[#C9A84C]" data-testid="contact-usa">
                <h3 className="font-bold text-[#1A1F4B] mb-4 flex items-center gap-2">
                  <span className="text-xl">🇺🇸</span>
                  {t('contact.usa.title')}
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3 text-[#1F2937]/70">
                    <Phone className="w-4 h-4 text-[#C9A84C]" />
                    <span>{t('contact.usa.phone')}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#1F2937]/70">
                    <Mail className="w-4 h-4 text-[#C9A84C]" />
                    <span>{t('contact.usa.email')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
